import { Upload } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import { useExperimentCategoryExclusions } from '../../hooks/use-experiments';
import type { CategoryExclusionMetric } from '../../types/experiment.types';

import { ExperimentDataUnavailable } from './experiment-data-unavailable';
import { RevenueSourceTag } from './revenue-source-tag';

interface Props {
  experimentId: string;
}

const COLUMNS: { key: CategoryExclusionMetric; labelKey: string }[] = [
  { key: 'gmcImpressions', labelKey: 'detail.health.categoryExclusions.columns.gmcImpressions' },
  { key: 'gmcClicks', labelKey: 'detail.health.categoryExclusions.columns.gmcClicks' },
  { key: 'sessions', labelKey: 'detail.health.categoryExclusions.columns.sessions' },
  {
    key: 'conversionValue',
    labelKey: 'detail.health.categoryExclusions.columns.conversionValue',
  },
  { key: 'revenue', labelKey: 'detail.health.categoryExclusions.columns.revenue' },
];

export function ExperimentCategoryExclusions({ experimentId }: Props) {
  const { t } = useTranslation('experiments');
  const { data, isLoading, isError } = useExperimentCategoryExclusions(experimentId);

  // Flattens the Control/Test matrix into one Control and one Test column per metric.
  const handleExport = () => {
    if (!data || data.rows.length === 0) return;
    const controlLabel = t('detail.health.categoryExclusions.control');
    const testLabel = t('detail.health.categoryExclusions.test');
    const csvRows = data.rows.map((row) => {
      const csvRow: Record<string, string> = { label: row.label };
      COLUMNS.forEach((col) => {
        csvRow[`${col.key}_control`] = row.values[col.key].control;
        csvRow[`${col.key}_test`] = row.values[col.key].test;
      });
      return csvRow;
    });
    downloadCsv('category-exclusions', csvRows, [
      { key: 'label', label: t('detail.health.categoryExclusions.metric') },
      ...COLUMNS.flatMap((col) => [
        { key: `${col.key}_control`, label: `${t(col.labelKey)} (${controlLabel})` },
        { key: `${col.key}_test`, label: `${t(col.labelKey)} (${testLabel})` },
      ]),
    ]);
  };

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="min-w-0 space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="flex flex-wrap items-center gap-2 text-[16px] font-medium wrap-break-word">
              {t('detail.health.categoryExclusions.title')}
              <RevenueSourceTag source={data?.revenueSource} />
            </h3>
            <p className="text-muted-foreground text-sm wrap-break-word">
              {t('detail.health.categoryExclusions.subtitle')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || isError || !data || data.rows.length === 0}
            className={cn(
              'gap-2',
              isLoading || isError || !data || data.rows.length === 0
                ? 'cursor-not-allowed'
                : 'cursor-pointer',
            )}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {t('detail.health.categoryExclusions.exportCsv')}
          </Button>
        </div>

        {/* Matrix — collapses to a "data unavailable" line when the source
            query throws (no backend endpoint yet). */}
        {isError ? (
          <ExperimentDataUnavailable />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-muted/30">
                {/* Row 1: metric group names (spans 2) */}
                <tr>
                  <th rowSpan={2} className="text-muted-foreground px-4 py-3 text-left font-medium">
                    {t('detail.health.categoryExclusions.metric')}
                  </th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      colSpan={2}
                      className={cn(
                        'text-muted-foreground px-4 py-2 text-center font-medium',
                        data?.highlightedColumn === col.key && 'bg-primary/10 text-primary',
                      )}
                    >
                      {t(col.labelKey)}
                    </th>
                  ))}
                </tr>
                {/* Row 2: Control / Test sub-headers */}
                <tr>
                  {COLUMNS.map((col) => (
                    <Fragment key={col.key}>
                      <th
                        className={cn(
                          'text-muted-foreground px-4 py-2 text-center text-xs font-medium',
                          data?.highlightedColumn === col.key && 'bg-primary/10 text-primary',
                        )}
                      >
                        {t('detail.health.categoryExclusions.control')}
                      </th>
                      <th
                        className={cn(
                          'text-muted-foreground px-4 py-2 text-center text-xs font-medium',
                          data?.highlightedColumn === col.key && 'bg-primary/10 text-primary',
                        )}
                      >
                        {t('detail.health.categoryExclusions.test')}
                      </th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading || !data
                  ? Array.from({ length: 2 }).map((_, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-4 py-4">
                          <Skeleton className="h-4 w-20" />
                        </td>
                        {COLUMNS.flatMap((c) => [c.key + '-c', c.key + '-t']).map((k) => (
                          <td key={k} className="px-4 py-4">
                            <Skeleton className="h-4 w-12" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : data.rows.map((row) => (
                      <tr key={row.label} className="border-t">
                        <td className="text-muted-foreground px-4 py-4">{row.label}</td>
                        {COLUMNS.map((col) => {
                          const highlighted = data.highlightedColumn === col.key;
                          return (
                            <Fragment key={col.key}>
                              <td
                                className={cn(
                                  'px-4 py-4 text-center',
                                  highlighted &&
                                    'bg-primary/5 border-primary/60 border-x border-y-0',
                                )}
                              >
                                {row.values[col.key].control}
                              </td>
                              <td
                                className={cn(
                                  'px-4 py-4 text-center',
                                  highlighted &&
                                    'bg-primary/5 border-primary/60 border-x border-y-0',
                                )}
                              >
                                {row.values[col.key].test}
                              </td>
                            </Fragment>
                          );
                        })}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
